// Compatibility facade for the (now-retired) Firestore service module.
//
// Every public name this file used to export is preserved so the dozens of
// component import sites (`import { studentService } from
// '../services/firebaseService'`) keep working. Internally each service now
// forwards to the api-backed V2 module — there is no Firebase code path
// left.
//
// The three legacy services that never got V2 ports (dailyLogService,
// curriculumService, smsService) are stubbed: they return empty results +
// log a warning, so the dashboards / reports they back render an empty
// state instead of crashing. Port to /api/* in a follow-up if those views
// need to come back.

import { classService as classServiceV2 } from './classServiceV2';
import { studentService as studentServiceV2 } from './studentServiceV2';
import { sessionService as sessionServiceV2 } from './sessionServiceV2';
import { attendanceService as attendanceServiceV2 } from './attendanceServiceV2';

export const classService = classServiceV2;
export const studentService = studentServiceV2;
export const sessionService = sessionServiceV2;
export const attendanceService = attendanceServiceV2;

// ---- Session pedagogy ---------------------------------------------------
// Three session types: NEW (today's new lesson), YESTERDAY_HIFZ (recap of
// yesterday's new), OLD (cumulative review of long-memorised material).
export const SESSION_TYPES = {
  NEW: 'NEW',
  YESTERDAY_HIFZ: 'YESTERDAY_HIFZ',
  OLD: 'OLD'
};

// Legacy reads from the old 'activityType' field still come through here.
// The legacy values predate the rename: 'NEW' / 'REVISION' / 'TIKRAR'.
export const mapLegacyActivityType = (activityType) => {
  switch (activityType) {
    case 'NEW': return SESSION_TYPES.NEW;
    case 'REVISION': return SESSION_TYPES.OLD;
    case 'TIKRAR': return SESSION_TYPES.YESTERDAY_HIFZ;
    default: return activityType;
  }
};

export const mapSessionTypeToLegacy = (sessionType) => {
  switch (sessionType) {
    case SESSION_TYPES.NEW: return 'NEW';
    case SESSION_TYPES.OLD: return 'REVISION';
    case SESSION_TYPES.YESTERDAY_HIFZ: return 'TIKRAR';
    default: return sessionType;
  }
};

// ---- Attendance ---------------------------------------------------------
// Definitions live in attendanceConstants.js so attendanceServiceV2.js can
// import them without forming a cycle through this file. We re-export here
// to keep the existing public surface intact.
export { ATTENDANCE_STATUSES, summarizeAttendance } from './attendanceConstants';

// ---- Quality-trend helper (red-flag detector) ---------------------------
export const isQualityDropAlert = (recentOveralls, threshold = 3, minCount = 3) => {
  if (!Array.isArray(recentOveralls)) return false;
  if (recentOveralls.length < minCount) return false;
  const lastN = recentOveralls.slice(-minCount);
  return lastN.every((v) => typeof v === 'number' && v < threshold);
};

// ---- Curriculum (pure helpers — no I/O) ---------------------------------
const TOTAL_RUB = 240;

export const computeExpectedProgress = ({
  startDate,
  dailyNewQuota,
  asOfDate = new Date()
}) => {
  if (!startDate || !dailyNewQuota || dailyNewQuota <= 0) return 0;
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const asOf = asOfDate instanceof Date ? asOfDate : new Date(asOfDate);
  const msPerDay = 1000 * 60 * 60 * 24;
  const days = Math.max(0, Math.floor((asOf - start) / msPerDay));
  return Math.min(TOTAL_RUB, Math.round(days * dailyNewQuota));
};

export const computeCurriculumStatus = ({
  plan,
  memorizedCount = 0,
  asOfDate = new Date()
}) => {
  if (!plan || !plan.startDate || !plan.dailyNewQuota) {
    return { status: 'unknown', expected: 0, memorized: memorizedCount, delta: 0 };
  }
  const expected = computeExpectedProgress({
    startDate: plan.startDate,
    dailyNewQuota: plan.dailyNewQuota,
    asOfDate
  });
  const delta = memorizedCount - expected;
  let status = 'onTrack';
  if (delta < -Math.max(1, plan.dailyNewQuota)) status = 'behind';
  else if (delta > Math.max(1, plan.dailyNewQuota)) status = 'ahead';
  return { status, expected, memorized: memorizedCount, delta };
};

// ---- Stubs for un-ported services ---------------------------------------
// These were Firestore-backed; the api backend never grew equivalents.
// Consumers (Reports.js, CurriculumPlanner.js) get empty results instead of
// the silent firebase 500. Wire them up to /api/* if the feature is needed.
const stubWarn = (name) => {
  // eslint-disable-next-line no-console
  console.warn(`${name}: stubbed — no api backend for this collection yet`);
};

export const dailyLogService = {
  async createLog() { stubWarn('dailyLogService.createLog'); return null; },
  async updateStudentMemorization() { stubWarn('dailyLogService.updateStudentMemorization'); },
  async getLogsByStudent() { stubWarn('dailyLogService.getLogsByStudent'); return []; },
  async getLogsByDate() { stubWarn('dailyLogService.getLogsByDate'); return []; },
  async getPreviousDayNewMemorization() {
    stubWarn('dailyLogService.getPreviousDayNewMemorization');
    return [];
  }
};

export const curriculumService = {
  async createPlan() { stubWarn('curriculumService.createPlan'); return null; },
  async updatePlan() { stubWarn('curriculumService.updatePlan'); },
  async deletePlan() { stubWarn('curriculumService.deletePlan'); },
  async getPlans() { stubWarn('curriculumService.getPlans'); return []; },
  async getPlanById() { stubWarn('curriculumService.getPlanById'); return null; },
  async getPlanForClass() { stubWarn('curriculumService.getPlanForClass'); return null; },
  async getClassProgressReport({ students = [] } = {}) {
    stubWarn('curriculumService.getClassProgressReport');
    const report = {};
    for (const s of students) {
      report[s.id] = {
        status: 'unknown',
        expected: 0,
        memorized: (s.memorizedPortions || []).length,
        delta: 0,
        planId: null,
        planName: null
      };
    }
    return { plan: null, report };
  }
};

export const smsService = {
  async sendProgressSMS() {
    stubWarn('smsService.sendProgressSMS');
    return { success: false, message: 'SMS provider not configured' };
  }
};
